/**
 * Transaction Review Queue Page - Hierarchical View
 * ==================================================
 * Dedicated page for data specialists to review all pending transaction clarifications.
 * 
 * Features:
 * - Dual view: "By Case" (grouped by Law Firm → Claim) or "All Items" (flat list)
 * - Client-Provided Clarifications (from Magic Link Portal)
 * - AI-Parsed Responses needing review
 * - Awaiting Client Response items
 * - Bulk approve with "Continue in Wizard" navigation
 * - Success toast with direct links to Wizard Step 3
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import AppLayout from '../components/AppLayout';
import { Toaster, toast } from 'sonner';
import {
  HelpCircle,
  CheckCircle2,
  AlertCircle,
  Clock,
  Inbox,
  Filter,
  RefreshCw,
  ExternalLink,
  Check,
  X,
  ChevronDown,
  ChevronRight,
  Loader2,
  UserCheck,
  MessageSquare,
  DollarSign,
  Calendar,
  Edit2,
  CheckSquare,
  Square,
  Building2,
  User,
  ArrowRight,
  LayoutList,
  LayoutGrid,
  Search
} from 'lucide-react';

interface Transaction {
  type: string;
  id: string;
  request_id?: string;
  report_id: string;
  claimant_name: string;
  law_firm_name?: string;
  claim_status?: string;
  transaction_id: string;
  question: string;
  client_response?: string;
  client_category?: string;
  client_notes?: string;
  ai_confidence?: number;
  received_at?: string;
  source?: string;
  transaction_date?: string;
  transaction_amount?: number;
  transaction_description?: string;
  current_category?: string;
  priority: 'high' | 'medium' | 'low';
  days_waiting?: number;
}

interface Claim {
  claimant_name: string;
  report_id: string;
  status: string;
  items: Transaction[];
  client_clarifications: Transaction[];
  total_items: number;
  client_clarification_count: number;
}

interface LawFirmGroup {
  law_firm_name: string;
  claims: Claim[];
  total_items: number;
  client_clarification_count: number;
  claims_count: number;
}

interface ReviewStats {
  needs_review_count: number;
  awaiting_response_count: number;
  client_clarification_count: number;
  total_count: number;
  law_firms_count?: number;
}

// BI Category options
const BI_CATEGORIES = [
  { group: 'Income', options: ['Revenue', 'Sales Revenue', 'Service Income', 'Other Income'] },
  { group: 'Fixed Costs', options: ['Rent & Occupancy', 'Utilities', 'Insurance', 'Technology & Software', 'Bank & Financial Fees', 'Taxes & Licenses', 'Debt Service'] },
  { group: 'Variable Costs', options: ['COGS', 'Payroll & Benefits', 'Professional Services', 'Marketing & Advertising', 'Office & Supplies', 'Travel & Entertainment', 'Vehicle & Transportation', 'Inventory & Merchandise', 'Food & Beverage Cost'] },
  { group: 'Other', options: ['Equipment Purchase', 'Owner Draw', 'Transfer', 'Miscellaneous', 'Uncategorized'] }
];

export default function TransactionReviewQueue() {
  const navigate = useNavigate();
  
  // View state
  const [viewMode, setViewMode] = useState<'grouped' | 'flat'>('grouped');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Data state
  const [groupedData, setGroupedData] = useState<LawFirmGroup[]>([]);
  const [flatItems, setFlatItems] = useState<Transaction[]>([]);
  const [clientClarifications, setClientClarifications] = useState<Transaction[]>([]);
  const [stats, setStats] = useState<ReviewStats | null>(null);
  const [loading, setLoading] = useState(true);
  
  // UI state
  const [expandedFirms, setExpandedFirms] = useState<Set<string>>(new Set());
  const [expandedClaims, setExpandedClaims] = useState<Set<string>>(new Set());
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [selectedClaims, setSelectedClaims] = useState<Map<string, Set<string>>>(new Map());
  
  // Processing state
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const [bulkApproving, setBulkApproving] = useState(false);
  const [correctedCategories, setCorrectedCategories] = useState<Record<string, string>>({});

  // Fetch queue data
  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get(`/api/v1/clarifications/review-queue?limit=100&view=${viewMode}`, { showErrorToast: false });
      
      if (viewMode === 'grouped' && data.grouped_data) {
        setGroupedData(data.grouped_data);
        // Auto-expand all firms initially
        setExpandedFirms(new Set(data.grouped_data.map((f: LawFirmGroup) => f.law_firm_name)));
      }
      
      setFlatItems(data.items || []);
      setClientClarifications(data.client_clarifications || []);
      setStats({
        needs_review_count: data.needs_review_count || 0,
        awaiting_response_count: data.awaiting_response_count || 0,
        client_clarification_count: data.client_clarification_count || 0,
        total_count: data.total_count || 0,
        law_firms_count: data.law_firms_count
      });
    } catch (err) {
      console.error('Failed to fetch review queue:', err);
      toast.error('Failed to load review queue');
    } finally {
      setLoading(false);
    }
  }, [viewMode]);

  useEffect(() => {
    fetchQueue();
    const interval = setInterval(fetchQueue, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [fetchQueue]);

  // Navigate to wizard Step 3 with transactions section
  const goToWizard = (reportId: string, claimantName: string, section: string = 'transactions') => {
    // Navigate to wizard with step=3 and specific section (deep link)
    navigate(`/wizard-v2?draft=${reportId}&step=3&section=${section}`);
    toast.success(`Opening ${claimantName} in Wizard...`);
  };

  // Handle single approve/reject
  const handleReview = async (
    responseId: string, 
    approved: boolean, 
    category?: string,
    claimantName?: string,
    reportId?: string
  ) => {
    setReviewingId(responseId);
    try {
      const params = new URLSearchParams({
        approved: String(approved),
        apply_to_draft: 'true',
        ...(category && !approved ? { corrected_category: category } : {})
      });

      const result = await api.post(`/api/v1/clarifications/responses/${responseId}/review?${params}`, null, { showErrorToast: false });
      
      // Remove from lists
      setClientClarifications(prev => prev.filter(item => item.id !== responseId));
      setFlatItems(prev => prev.filter(item => item.id !== responseId));
      setSelectedItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(responseId);
        return newSet;
      });
        
        // Update stats
        if (stats) {
          setStats({
            ...stats,
            client_clarification_count: Math.max(0, stats.client_clarification_count - 1),
            total_count: Math.max(0, stats.total_count - 1)
          });
        }
        
        // Show success toast with navigation option
        toast.success(
          <div className="flex flex-col gap-2">
            <span>Transaction {approved ? 'approved' : 'updated'} for {claimantName || 'claim'}</span>
            {reportId && (
              <button
                onClick={() => goToWizard(reportId, claimantName || 'Claim')}
                className="flex items-center gap-1 text-sm text-emerald-700 hover:text-emerald-800 font-medium"
              >
                Continue in Wizard <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>,
          { duration: 5000 }
        );
        
        // Refresh to update grouped view
        if (viewMode === 'grouped') {
          fetchQueue();
        }
    } catch (err) {
      console.error('Failed to review response:', err);
      toast.error('Failed to process review');
    } finally {
      setReviewingId(null);
    }
  };

  // Handle bulk approve for a specific claim
  const handleBulkApproveClaim = async (claim: Claim) => {
    if (claim.client_clarifications.length === 0) return;
    
    setBulkApproving(true);
    try {
      const items = claim.client_clarifications.map(item => ({
        response_id: item.id,
        approved: true
      }));

      const result = await api.post('/api/v1/clarifications/responses/bulk-approve', { items, apply_to_draft: true }, { showErrorToast: false });
      
      toast.success(
        <div className="flex flex-col gap-2">
          <span>{result.approved_count} transactions approved for {claim.claimant_name}</span>
          <button
            onClick={() => goToWizard(claim.report_id, claim.claimant_name)}
            className="flex items-center gap-1 text-sm text-emerald-700 hover:text-emerald-800 font-medium"
          >
            Continue in Wizard <ArrowRight className="w-4 h-4" />
          </button>
        </div>,
        { duration: 6000 }
      );
      
      // Refresh data
      fetchQueue();
    } catch (err) {
      console.error('Failed to bulk approve:', err);
      toast.error('Failed to approve transactions');
    } finally {
      setBulkApproving(false);
    }
  };

  // Handle bulk approve for selected items (flat view)
  const handleBulkApproveSelected = async () => {
    if (selectedItems.size === 0) return;
    
    setBulkApproving(true);
    try {
      const items = Array.from(selectedItems).map(id => ({
        response_id: id,
        approved: true
      }));

      const result = await api.post('/api/v1/clarifications/responses/bulk-approve', { items, apply_to_draft: true }, { showErrorToast: false });
      
      // Get unique claims affected
      const affectedClaims = new Set<string>();
      clientClarifications.forEach(item => {
        if (selectedItems.has(item.id)) {
          affectedClaims.add(item.claimant_name);
        }
      });
      
      if (affectedClaims.size === 1) {
        const claimName = Array.from(affectedClaims)[0];
        const item = clientClarifications.find(c => selectedItems.has(c.id));
        toast.success(
          <div className="flex flex-col gap-2">
            <span>{result.approved_count} transactions approved for {claimName}</span>
            {item && (
              <button
                onClick={() => goToWizard(item.report_id, claimName)}
                className="flex items-center gap-1 text-sm text-emerald-700 hover:text-emerald-800 font-medium"
              >
                Continue in Wizard <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>,
          { duration: 6000 }
        );
      } else {
        toast.success(`${result.approved_count} transactions approved across ${affectedClaims.size} claims`, {
          duration: 4000
        });
      }
      
      setSelectedItems(new Set());
      fetchQueue();
    } catch (err) {
      console.error('Failed to bulk approve:', err);
      toast.error('Failed to approve transactions');
    } finally {
      setBulkApproving(false);
    }
  };

  // Toggle functions
  const toggleFirm = (firmName: string) => {
    setExpandedFirms(prev => {
      const next = new Set(prev);
      if (next.has(firmName)) next.delete(firmName);
      else next.add(firmName);
      return next;
    });
  };

  const toggleClaim = (claimKey: string) => {
    setExpandedClaims(prev => {
      const next = new Set(prev);
      if (next.has(claimKey)) next.delete(claimKey);
      else next.add(claimKey);
      return next;
    });
  };

  const toggleItem = (id: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelect = (id: string) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Format helpers
  const formatAmount = (amount?: number) => {
    if (amount === undefined || amount === null) return '-';
    const formatted = Math.abs(amount).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    return amount < 0 ? `-${formatted}` : formatted;
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      draft: 'bg-amber-100 text-amber-700',
      completed: 'bg-green-100 text-green-700',
      unknown: 'bg-gray-100 text-gray-600'
    };
    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${styles[status] || styles.unknown}`}>
        {status}
      </span>
    );
  };

  // Filter data based on search
  const filterBySearch = (items: Transaction[]) => {
    if (!searchQuery.trim()) return items;
    const query = searchQuery.toLowerCase();
    return items.filter(item => 
      item.claimant_name?.toLowerCase().includes(query) ||
      item.law_firm_name?.toLowerCase().includes(query) ||
      item.transaction_description?.toLowerCase().includes(query) ||
      item.report_id?.toLowerCase().includes(query)
    );
  };

  const filteredGroupedData = groupedData.map(firm => ({
    ...firm,
    claims: firm.claims.filter(claim => {
      if (!searchQuery.trim()) return true;
      const query = searchQuery.toLowerCase();
      return claim.claimant_name?.toLowerCase().includes(query) ||
             firm.law_firm_name?.toLowerCase().includes(query) ||
             claim.report_id?.toLowerCase().includes(query);
    })
  })).filter(firm => firm.claims.length > 0);

  // Render transaction card
  const renderTransactionCard = (item: Transaction, showClaimInfo: boolean = false) => {
    const isSelected = selectedItems.has(item.id);
    const isExpanded = expandedItems.has(item.id);
    const isClientClarification = item.type === 'client_clarification';

    return (
      <div
        key={item.id}
        data-testid={`transaction-${item.id}`}
        className={`bg-white rounded-lg border-2 transition-all ${
          isSelected 
            ? 'border-emerald-400 ring-2 ring-emerald-100' 
            : isClientClarification
              ? 'border-emerald-200 hover:border-emerald-300'
              : 'border-gray-200 hover:border-gray-300'
        }`}
      >
        <div className="p-4">
          <div className="flex items-start gap-3">
            {/* Checkbox */}
            {isClientClarification && (
              <button onClick={() => toggleSelect(item.id)} className="mt-1 flex-shrink-0">
                {isSelected ? (
                  <CheckSquare className="w-5 h-5 text-emerald-600" />
                ) : (
                  <Square className="w-5 h-5 text-gray-400 hover:text-gray-600" />
                )}
              </button>
            )}

            {/* Content */}
            <div className="flex-1 min-w-0">
              {/* Header row with claim info if needed */}
              {showClaimInfo && (
                <div className="flex items-center gap-2 mb-2 text-sm">
                  <button
                    onClick={() => goToWizard(item.report_id, item.claimant_name)}
                    className="font-semibold text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {item.claimant_name}
                  </button>
                  {item.law_firm_name && (
                    <span className="text-gray-500">• {item.law_firm_name}</span>
                  )}
                </div>
              )}

              {/* Transaction details */}
              <div className="bg-gray-50 rounded-lg p-3 mb-3">
                <div className="flex items-center gap-4 text-sm mb-2">
                  {item.transaction_date && (
                    <span className="flex items-center gap-1 text-gray-600">
                      <Calendar className="w-4 h-4" />
                      {item.transaction_date}
                    </span>
                  )}
                  {item.transaction_amount !== undefined && (
                    <span className={`flex items-center gap-1 font-semibold ${
                      item.transaction_amount < 0 ? 'text-red-600' : 'text-green-600'
                    }`}>
                      <DollarSign className="w-4 h-4" />
                      {formatAmount(item.transaction_amount)}
                    </span>
                  )}
                </div>
                <p className="text-gray-900 font-medium">
                  {item.transaction_description || 'No description'}
                </p>
                {item.current_category && (
                  <p className="text-sm text-gray-500 mt-1">
                    Previous: <span className="text-indigo-600">{item.current_category}</span>
                  </p>
                )}
              </div>

              {/* Client selection (if client clarification) */}
              {isClientClarification && (
                <div className="flex items-start gap-4">
                  <div className="flex-1 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                    <p className="text-xs text-emerald-700 font-medium mb-1 flex items-center gap-1">
                      <UserCheck className="w-3 h-3" />
                      Client Selected:
                    </p>
                    <p className="text-lg font-semibold text-emerald-900">
                      {item.client_category}
                    </p>
                    {item.client_notes && (
                      <div className="mt-2 pt-2 border-t border-emerald-200">
                        <p className="text-xs text-emerald-700 font-medium mb-1 flex items-center gap-1">
                          <MessageSquare className="w-3 h-3" />
                          Client Notes:
                        </p>
                        <p className="text-sm text-emerald-800">"{item.client_notes}"</p>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() => handleReview(item.id, true, undefined, item.claimant_name, item.report_id)}
                      disabled={reviewingId === item.id}
                      className="px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
                    >
                      {reviewingId === item.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Check className="w-4 h-4" />
                      )}
                      Approve
                    </button>
                    <button
                      onClick={() => toggleItem(item.id)}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 flex items-center gap-2"
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit
                    </button>
                  </div>
                </div>
              )}

              {/* Awaiting response state */}
              {item.type === 'awaiting_response' && (
                <div className="flex items-center justify-between bg-amber-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-amber-700">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm">Awaiting client response</span>
                    {item.days_waiting !== undefined && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        item.days_waiting > 7 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {item.days_waiting}d waiting
                      </span>
                    )}
                  </div>
                  <button className="text-sm text-amber-700 hover:text-amber-900 font-medium">
                    Send Reminder
                  </button>
                </div>
              )}

              {/* Expanded edit section */}
              {isExpanded && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-sm font-medium text-gray-700 mb-2">Change category to:</p>
                  <div className="flex gap-2">
                    <select
                      value={correctedCategories[item.id] || ''}
                      onChange={e => setCorrectedCategories(prev => ({
                        ...prev,
                        [item.id]: e.target.value
                      }))}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                    >
                      <option value="">Select different category...</option>
                      {BI_CATEGORIES.map(group => (
                        <optgroup key={group.group} label={group.group}>
                          {group.options.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    <button
                      onClick={() => handleReview(item.id, false, correctedCategories[item.id], item.claimant_name, item.report_id)}
                      disabled={!correctedCategories[item.id] || reviewingId === item.id}
                      className="px-4 py-2 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 disabled:opacity-50"
                    >
                      Apply Change
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* External link */}
            <button
              onClick={() => goToWizard(item.report_id, item.claimant_name)}
              className="p-2 hover:bg-gray-100 rounded-lg flex-shrink-0"
              title="Open in Wizard"
            >
              <ExternalLink className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <AppLayout>
      <Toaster position="top-right" richColors />
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                <HelpCircle className="w-7 h-7 text-amber-600" />
                Transaction Review Queue
              </h1>
              <p className="text-gray-600 mt-1">
                Review client-provided clarifications and approve transactions
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* View Toggle */}
              <div className="flex items-center bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grouped')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-1.5 transition ${
                    viewMode === 'grouped' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <LayoutGrid className="w-4 h-4" />
                  By Case
                </button>
                <button
                  onClick={() => setViewMode('flat')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-1.5 transition ${
                    viewMode === 'flat' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <LayoutList className="w-4 h-4" />
                  All Items
                </button>
              </div>
              <button
                onClick={fetchQueue}
                disabled={loading}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-2 transition disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl border border-emerald-200 p-4 ring-2 ring-emerald-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <UserCheck className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.client_clarification_count}</p>
                  <p className="text-sm text-emerald-700 font-medium">Client Clarifications</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.awaiting_response_count}</p>
                  <p className="text-sm text-gray-600">Awaiting Response</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Building2 className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.law_firms_count || groupedData.length}</p>
                  <p className="text-sm text-gray-600">Law Firms</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <Inbox className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_count}</p>
                  <p className="text-sm text-gray-600">Total Items</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by law firm, client name, or claim ID..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
        </div>

        {/* Bulk actions for flat view */}
        {viewMode === 'flat' && selectedItems.size > 0 && (
          <div className="mb-4 p-4 bg-emerald-50 rounded-lg flex items-center justify-between">
            <span className="text-emerald-800 font-medium">
              {selectedItems.size} item(s) selected
            </span>
            <button
              onClick={handleBulkApproveSelected}
              disabled={bulkApproving}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
            >
              {bulkApproving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              Approve Selected
            </button>
          </div>
        )}

        {/* Main Content */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
          </div>
        ) : viewMode === 'grouped' ? (
          /* GROUPED VIEW */
          <div className="space-y-4">
            {filteredGroupedData.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-gray-900 mb-1">All Caught Up!</h3>
                <p className="text-gray-600">No items currently need your attention.</p>
              </div>
            ) : (
              filteredGroupedData.map(firm => (
                <div key={firm.law_firm_name} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  {/* Law Firm Header */}
                  <button
                    onClick={() => toggleFirm(firm.law_firm_name)}
                    className="w-full p-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition"
                  >
                    <div className="flex items-center gap-3">
                      {expandedFirms.has(firm.law_firm_name) ? (
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-500" />
                      )}
                      <Building2 className="w-5 h-5 text-blue-600" />
                      <span className="font-semibold text-gray-900">{firm.law_firm_name}</span>
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                        {firm.claims_count} claim{firm.claims_count !== 1 ? 's' : ''}
                      </span>
                      {firm.client_clarification_count > 0 && (
                        <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                          {firm.client_clarification_count} pending
                        </span>
                      )}
                    </div>
                    <span className="text-sm text-gray-500">
                      {firm.total_items} item{firm.total_items !== 1 ? 's' : ''}
                    </span>
                  </button>

                  {/* Claims under this firm */}
                  {expandedFirms.has(firm.law_firm_name) && (
                    <div className="divide-y divide-gray-100">
                      {firm.claims.map(claim => {
                        const claimKey = `${claim.claimant_name}_${claim.report_id}`;
                        const isClaimExpanded = expandedClaims.has(claimKey);

                        return (
                          <div key={claimKey} className="border-l-4 border-transparent hover:border-emerald-400">
                            {/* Claim Header */}
                            <div className="p-4 pl-12 flex items-center justify-between">
                              <button
                                onClick={() => toggleClaim(claimKey)}
                                className="flex items-center gap-3 flex-1"
                              >
                                {isClaimExpanded ? (
                                  <ChevronDown className="w-4 h-4 text-gray-400" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-gray-400" />
                                )}
                                <User className="w-4 h-4 text-gray-500" />
                                <span className="font-medium text-gray-900">{claim.claimant_name}</span>
                                {getStatusBadge(claim.status)}
                                {claim.client_clarification_count > 0 && (
                                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                                    {claim.client_clarification_count} to review
                                  </span>
                                )}
                              </button>
                              <div className="flex items-center gap-2">
                                {/* Go to Wizard - only enabled when all items are approved */}
                                {claim.client_clarification_count > 0 ? (
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-amber-600 italic">
                                      Approve {claim.client_clarification_count} item{claim.client_clarification_count !== 1 ? 's' : ''} to continue
                                    </span>
                                    <button
                                      disabled
                                      className="px-3 py-1.5 bg-gray-300 text-gray-500 text-sm rounded-lg font-medium cursor-not-allowed flex items-center gap-1.5"
                                      title="Approve all clarifications first"
                                    >
                                      Go to Wizard <ArrowRight className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => goToWizard(claim.report_id, claim.claimant_name)}
                                    className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg font-medium hover:bg-blue-700 flex items-center gap-1.5"
                                  >
                                    Go to Wizard <ArrowRight className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Transactions under this claim */}
                            {isClaimExpanded && (
                              <div className="px-4 pb-4 pl-16 space-y-3">
                                {claim.client_clarifications.map(item => renderTransactionCard(item, false))}
                                {claim.items.map(item => renderTransactionCard(item, false))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        ) : (
          /* FLAT VIEW */
          <div className="space-y-4">
            {/* Client Clarifications */}
            {filterBySearch(clientClarifications).length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <UserCheck className="w-5 h-5 text-emerald-600" />
                  Client Clarifications
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-sm rounded-full">
                    {filterBySearch(clientClarifications).length} pending
                  </span>
                </h2>
                <div className="space-y-3">
                  {filterBySearch(clientClarifications).map(item => renderTransactionCard(item, true))}
                </div>
              </div>
            )}

            {/* Other Items */}
            {filterBySearch(flatItems).length > 0 && (
              <div className={clientClarifications.length > 0 ? 'mt-8' : ''}>
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-amber-600" />
                  Awaiting Response
                </h2>
                <div className="space-y-3">
                  {filterBySearch(flatItems).map(item => renderTransactionCard(item, true))}
                </div>
              </div>
            )}

            {/* Empty State */}
            {filterBySearch(clientClarifications).length === 0 && filterBySearch(flatItems).length === 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-gray-900 mb-1">All Caught Up!</h3>
                <p className="text-gray-600">No items currently need your attention.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
